import type { ChatStatus, UIDataTypes, UIMessagePart, UITools } from 'ai';

import {
  isClarificationPending,
  isClarificationToolPart,
  type ClarificationAwareMessage,
  type ClarificationToolPart,
} from './clarification';
import {
  isNovaScheduleProposalToolPart,
  NOVA_SCHEDULE_PROPOSAL_TOOL_NAME,
  type NovaScheduleProposalToolPart,
} from './nova-schedule-proposal';

export const TOOL_HANDOFF_NAMES = ['clarification', NOVA_SCHEDULE_PROPOSAL_TOOL_NAME] as const;

export type ToolHandoffName = (typeof TOOL_HANDOFF_NAMES)[number];
export type ToolHandoffPart = ClarificationToolPart | NovaScheduleProposalToolPart;
export type ToolHandoffAwareMessage = ClarificationAwareMessage;

const TOOL_HANDOFF_NAME_SET = new Set<string>(TOOL_HANDOFF_NAMES);

export function isToolHandoffName(toolName: string): toolName is ToolHandoffName {
  return TOOL_HANDOFF_NAME_SET.has(toolName);
}

export function isToolHandoffPart(part: UIMessagePart<UIDataTypes, UITools>): part is ToolHandoffPart {
  return isClarificationToolPart(part) || isNovaScheduleProposalToolPart(part);
}

export function getToolHandoffPart(message?: ToolHandoffAwareMessage | null): ToolHandoffPart | undefined {
  return message?.parts.slice().reverse().find(isToolHandoffPart);
}

export function isToolHandoffPending(message?: ToolHandoffAwareMessage | null): boolean {
  const part = getToolHandoffPart(message);

  if (part?.state === 'input-available') {
    return true;
  }

  if (part?.state === 'output-available') {
    return false;
  }

  return isClarificationPending(message);
}

export function getLatestPendingToolHandoff<M extends ToolHandoffAwareMessage>(
  messages: readonly M[],
): { message: M; part: ToolHandoffPart } | undefined {
  const message = messages.at(-1);

  if (message?.role !== 'assistant') {
    return undefined;
  }

  const part = getToolHandoffPart(message);
  return part?.state === 'input-available' ? { message, part } : undefined;
}

export function isToolHandoffBlocking(
  message: ToolHandoffAwareMessage,
  { chatStatus, isLatest }: { chatStatus: ChatStatus; isLatest: boolean },
): boolean {
  if (isToolHandoffPending(message)) {
    return true;
  }

  const part = getToolHandoffPart(message);
  return isLatest && chatStatus !== 'ready' && part?.state === 'output-available';
}

export function lastAssistantMessageHasToolHandoffInLastStep(messages: readonly ToolHandoffAwareMessage[]): boolean {
  const message = messages.at(-1);

  if (message?.role !== 'assistant') {
    return false;
  }

  const lastStepStartIndex = message.parts.reduce(
    (lastIndex, part, index) => (part.type === 'step-start' ? index : lastIndex),
    -1,
  );

  return message.parts.slice(lastStepStartIndex + 1).some(isToolHandoffPart);
}
